import { getChatArtifact } from '../../api/chatApi';
import { buildArtifactDisplayModel } from './chatArtifactDisplayModel';
import {
  artifactMessageContent, candidateToCompany, discoveryCompleteMessage, ensureOkResponse,
  isArtifactActionTurn, isTerminalJobStatus, progressPatchFromJob, responseMessage, suggestionsFromPayload,
} from './chatMessageModel';
import { leadJobOutcomeNotice } from './chatArtifactLeadModel';
import { ownerOfferActions } from './chatEnrichmentOfferModel';
import { createOwnerProgressObserver } from './ownerProgressState';

export function artifactTurnDisplayPatch(artifactData, message, artifactId) {
  const companies = (artifactData.candidates || []).map(candidateToCompany);
  const artifactDisplay = buildArtifactDisplayModel({ artifactData, message, fallbackCompanies: companies });
  return {
    artifactId, companies, artifactDisplay,
    artifactAnswerActions: ownerOfferActions(message.artifactAnswerActions, artifactData.artifact),
  };
}

// Shared by live submits and history restores: only this turn receives the job's
// version of the list. The discovery turn never receives a paid action's result.
export async function finishArtifactActionTurn({
  messageId, jobId, artifactId, message, initialJob, waitForJob, updateAssistantMessage,
  refreshCredits, isCancelled = () => false, inPlaceSample = false, leadJob = false,
}) {
  let terminal = false;
  const patch = (value) => { if (!isCancelled()) updateAssistantMessage(messageId, value); };
  // #798: rows settle one by one, so the turn shows them as the owner search runs
  // and keeps whatever settled when the run is interrupted. The offers stay on the list card.
  const refreshRows = async (id = artifactId) => {
    const { artifactId: _unchanged, artifactAnswerActions: _offers, ...display } = artifactTurnDisplayPatch(
      ensureOkResponse(await getChatArtifact(id, { jobId }), 'Unable to refresh owner results.'), message, id);
    return display;
  };
  const observer = inPlaceSample ? null
    : createOwnerProgressObserver({ publish: patch, isCancelled, refresh: refreshRows });
  const isLeadJob = leadJob || initialJob?.job_type === 'artifact_lead_enrichment';
  try {
    const job = jobId ? await waitForJob(jobId, {
      initialJob, isCancelled, ...(isLeadJob ? { maxPollingChunks: 12 } : {}),
      onProgress: (data) => {
        if (inPlaceSample) return;
        patch(progressPatchFromJob(data, { isActionTurn: true }));
        observer.observe(data);
      },
    }) : initialJob;
    await observer?.finish();
    if (isCancelled()) return false;
    terminal = !jobId || isTerminalJobStatus(job?.status);
    if (!terminal) return false;
    refreshCredits?.();
    if (jobId && !['done', 'completed_with_errors'].includes(job.status)) {
      const errorText = leadJobOutcomeNotice(job)?.message
        || responseMessage(job, 'Artifact action did not complete.');
      const settled = !inPlaceSample && job.job_type === 'artifact_lead_enrichment' && job.artifact_id
        ? await refreshRows(job.artifact_id).catch(() => ({})) : {};
      if (isCancelled()) return true;
      updateAssistantMessage(messageId, inPlaceSample ? { artifactActionError: errorText } : {
        ...settled, ...progressPatchFromJob(job, { isActionTurn: true }), answerConfirmed: false,
        content: errorText, artifactActionError: null, suggestions: suggestionsFromPayload(job),
      });
      return true;
    }
    const resultId = job.artifact_id || initialJob?.artifact_id || artifactId;
    const response = jobId && !inPlaceSample ? await getChatArtifact(resultId, { jobId }) : await getChatArtifact(resultId);
    const data = ensureOkResponse(response, 'Unable to load updated artifact.');
    if (isCancelled()) return true;
    const display = artifactTurnDisplayPatch(data, message, resultId);
    const leadOutcome = leadJobOutcomeNotice(job);
    // #796: the fee-aware lead notice explains what was charged; the backend copy backs it.
    const outcome = leadOutcome?.message || job.status_message
      || responseMessage(job, responseMessage(initialJob, 'Artifact action completed.'));
    updateAssistantMessage(messageId, {
      ...display, ...(!inPlaceSample ? { artifactAnswerActions: [] } : {}),
      ...(inPlaceSample && !jobId ? {} : progressPatchFromJob(job, { isActionTurn: !inPlaceSample })),
      ...(!inPlaceSample ? { jobId, jobArtifactId: resultId } : jobId ? { jobId } : {}),
      answerConfirmed: true,
      content: inPlaceSample
        ? artifactMessageContent(display.artifactDisplay, discoveryCompleteMessage(display.companies)) : outcome,
      suggestions: suggestionsFromPayload(job),
      ...(!inPlaceSample || resultId !== artifactId ? { artifactWorkspace: null } : {}),
      artifactActionNotice: inPlaceSample ? outcome : null,
      artifactActionNoticeSeverity: (inPlaceSample && leadOutcome?.severity)
        || (job.status === 'completed_with_errors' ? 'warning' : 'success'),
    });
    return true;
  } catch (error) {
    await observer?.finish();
    if (!isCancelled()) {
      if (!terminal) refreshCredits?.();
      updateAssistantMessage(messageId, {
        ...(!inPlaceSample ? { jobStatus: 'failed', progressLabel: null, answerConfirmed: false,
          content: error.message || 'Unable to run this artifact action.' } : {}),
        artifactActionError: inPlaceSample ? error.message || 'Unable to run this artifact action.' : null,
      });
    }
    return true;
  }
}

export async function hydrateLatestArtifactTurn({ artifactId, messages, isCancelled, updateAssistantMessage }) {
  const owner = [...messages].reverse().find((message) => message.artifactId || message.artifactDisplay?.artifactId);
  if (!owner) return;
  const ownsJob = isArtifactActionTurn(owner) && (owner.jobArtifactId || owner.artifactId) === artifactId && owner.jobId;
  const response = ownsJob ? await getChatArtifact(artifactId, { jobId: owner.jobId }) : await getChatArtifact(artifactId);
  const data = ensureOkResponse(response, 'Unable to load the current company list.');
  if (isCancelled() || data.artifact?.artifact_id !== artifactId) return;
  const display = artifactTurnDisplayPatch(data, owner, artifactId);
  updateAssistantMessage(owner.id, {
    ...display,
    content: isArtifactActionTurn(owner) ? owner.content
      : artifactMessageContent(display.artifactDisplay, discoveryCompleteMessage(display.companies)),
  });
}

export function removeActionFlight(flights, flight) {
  return Object.fromEntries(Object.entries(flights || {}).filter(([, item]) =>
    item !== flight && (!flight.jobId || item.jobId !== flight.jobId)));
}

// A restored action runs independently of discovery processing. Reapplying history
// after a concurrent send shares the live submit's registry instead of polling twice.
export function resumeArtifactActionTurns({ messages, activeJobs, activeSession, sessionId,
  setArtifactActionInFlight, waitForJob, refreshCredits, updateAssistantMessage, isMounted }) {
  for (const message of messages.filter((item) => isArtifactActionTurn(item) && item.jobId
    && !isTerminalJobStatus(item.jobStatus) && item.jobStatus !== 'awaiting_approval')) {
    const artifactId = message.artifactId || message.referencedArtifactId;
    const owner = messages.find((item) => item.id !== message.id && item.artifactId === artifactId) || message;
    const flight = { artifactId, key: message.artifactActionKey, jobId: message.jobId };
    setArtifactActionInFlight((previous) => ({ ...previous, [owner.id]: flight }));
    if (activeJobs.current.has(message.jobId)) continue;
    activeJobs.current.add(message.jobId);
    finishArtifactActionTurn({ messageId: message.id, jobId: message.jobId, artifactId, message,
      initialJob: { job_id: message.jobId, status: message.jobStatus, job_type: message.jobType }, waitForJob, refreshCredits,
      leadJob: message.artifactActionKey === 'lead_finder_contacts',
      isCancelled: () => !isMounted(),
      updateAssistantMessage: (id, patch) => {
        if (activeSession.current === sessionId) updateAssistantMessage(id, patch);
      },
    }).then(() => {
      // The registry owns a polling attempt, not the lifetime of the backend job.
      activeJobs.current.delete(message.jobId);
      setArtifactActionInFlight((previous) => removeActionFlight(previous, flight));
    });
  }
}

// History may already contain the acknowledged pair. Retire both optimistic
// messages together rather than assigning a second message the same React key.
export function acknowledgeActionTurn(messages, {
  optimisticUserId, optimisticAssistantId, userId, assistantId, jobId,
}) {
  const persisted = messages.filter((item) => item.id !== optimisticUserId && item.id !== optimisticAssistantId);
  if (persisted.some((item) => item.id === userId || item.id === assistantId)) return persisted;
  return messages.map((item) => item.id === optimisticUserId
    ? { ...item, id: userId }
    : item.id === optimisticAssistantId ? { ...item, id: assistantId, jobId: jobId || null } : item);
}
